import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Calculator, LogOut, TrendingUp, ClipboardCheck, CheckCircle2, ChevronLeft, ChevronRight, Users, Heart, ArrowLeft, ListTodo, User, Sparkles, X, LineChart, Mail, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import { useMobileNav } from '../contexts/MobileNavContext';
import { usePermissions } from '../contexts/PermissionContext';
import axios from 'axios';
import './Sidebar.css';

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const { isAdvisor, currentBusiness, currentBusinessId, loading, clearBusiness } = useBusinessContext();
    const { isMobileMenuOpen, isMobile, closeMobileMenu, isSidebarCollapsed, toggleSidebarCollapse } = useMobileNav();
    const { hasPermission, isViewer } = usePermissions();
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);

    // Advisor-only menu items
    const advisorMenuItems = [
        { path: '/advisor', icon: Users, label: 'My Businesses' }
    ];

    // Standard business menu items with permission keys
    const businessMenuItems = [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'can_view_dashboard' },
        { path: '/business-profile', icon: User, label: 'Client Profile', permission: 'can_view_business_profile' },
        { path: '/gap-analysis', icon: ClipboardCheck, label: 'Business Gap Assessment', permission: 'can_view_gap_assessments' },
        { path: '/personal-gap-analysis', icon: Heart, label: 'Personal Gap Assessment', permission: 'can_view_personal_gap' },
        { path: '/valuation', icon: Calculator, label: 'Valuation', permission: 'can_view_valuation' },
        { path: '/valuation-dashboard', icon: TrendingUp, label: 'Valuation Dashboard', permission: 'can_view_valuation' },
        { path: '/task-manager', icon: ListTodo, label: 'Task Manager', permission: 'can_view_tasks' },
        { path: '/envisioning', icon: Sparkles, label: 'Envisioning', permission: 'can_view_envisioning' },
        { path: '/inbox', icon: Mail, label: 'Inbox', permission: 'can_view_messages' },
    ];

    // Management menu items (only for non-viewers)
    const managementMenuItems = !isViewer && (isAdvisor || user?.role === 'business_owner') && (currentBusiness || currentBusinessId) ? [
        { path: '/manage-viewers', icon: Shield, label: 'Manage Viewers', permission: null }
    ] : [];

    // Filter business menu items based on permissions
    const filteredBusinessMenuItems = businessMenuItems.filter(item =>
        !item.permission || hasPermission(item.permission)
    );

    // Combine menu items with advisor link if needed
    const businessMenuWithAdvisorLink = isAdvisor && (currentBusiness || currentBusinessId)
        ? [{ path: '/advisor', icon: Users, label: 'My Businesses' }, ...filteredBusinessMenuItems, ...managementMenuItems]
        : [...filteredBusinessMenuItems, ...managementMenuItems];

    // Determine which menu to show
    // If on /advisor route, always show advisor menu (even if business context exists)
    // Otherwise, show advisor menu only if advisor with no business context
    const isOnAdvisorRoute = location.pathname === '/advisor';
    const shouldShowAdvisorMenu = isOnAdvisorRoute || (isAdvisor && !currentBusiness && !currentBusinessId && !loading);
    const menuItems = shouldShowAdvisorMenu ? advisorMenuItems : businessMenuWithAdvisorLink;

    const isActive = (path) => location.pathname === path;

    const handleBackToBusinesses = () => {
        clearBusiness();
        navigate('/advisor');
    };

    // Clear business context when clicking "My Businesses" menu item
    const handleMenuClick = (path) => {
        if (path === '/advisor' && isAdvisor) {
            clearBusiness();
        }
        // Close mobile menu when clicking a link
        if (isMobile) {
            closeMobileMenu();
        }
    };

    // Close mobile menu on route change
    useEffect(() => {
        if (isMobile) {
            closeMobileMenu();
        }
    }, [location.pathname]);

    // Fetch unread message count
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const businessId = currentBusiness?.id || currentBusinessId;

                // For viewers and advisors, wait until business context is set
                // Business owners always have their own business context
                if ((user?.role === 'viewer' || user?.role === 'advisor') && !businessId) {
                    return;
                }

                const headers = { 'Authorization': `Bearer ${token}` };

                // Include business ID if available (for users, or advisors viewing specific business)
                if (businessId) {
                    headers['X-Business-Id'] = businessId;
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/messages/`,
                    { headers }
                );

                setUnreadMessageCount(response.data.unread_count || 0);
            } catch (error) {
                // Silently fail - don't show errors for unread count
            }
        };

        fetchUnreadCount();

        // Refresh count every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [currentBusiness?.id, currentBusinessId, isAdvisor, user, location.pathname]);

    // Shared content component for both mobile and desktop
    const SidebarContent = ({ isMobileVersion = false }) => (
        <>
            {/* Logo/Brand and Toggle/Close */}
            <div className="sidebar-header p-4 sm:p-6 border-b flex items-center justify-between" style={{ borderColor: '#2d4a44' }}>
                {(!isSidebarCollapsed || isMobileVersion) && <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#c49e70' }}>Exit Ready Pro</h1>}
                {isMobileVersion ? (
                    <button
                        onClick={closeMobileMenu}
                        className="p-2 rounded-lg transition hover:opacity-80"
                        style={{ color: '#c49e70' }}
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                ) : (
                    <button
                        onClick={toggleSidebarCollapse}
                        className="p-2 rounded-lg transition hover:opacity-80"
                        style={{ color: '#c49e70' }}
                        title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>
                )}
            </div>

            {/* Business Context Banner for Advisors */}
            {isAdvisor && currentBusiness && (!isSidebarCollapsed || isMobileVersion) && (
                <div className="business-context-banner px-3 sm:px-4 pt-4 pb-2">
                    <div className="banner-content rounded-lg p-3 border" style={{ backgroundColor: 'rgba(196, 158, 112, 0.15)', borderColor: '#c49e70' }}>
                        <div className="banner-label text-xs mb-1" style={{ color: '#f1e9dd', opacity: 0.7 }}>Viewing Business:</div>
                        <div className="banner-business-name font-semibold text-sm truncate" style={{ color: '#f1e9dd' }}>
                            {currentBusiness.business_name || 'Unnamed Business'}
                        </div>
                        <button
                            onClick={handleBackToBusinesses}
                            className="banner-back-button mt-2 text-xs flex items-center gap-1 hover:opacity-80"
                            style={{ color: '#c49e70' }}
                        >
                            <ArrowLeft size={12} />
                            Back to Businesses
                        </button>
                    </div>
                </div>
            )}

            {/* Menu Items */}
            <nav className="sidebar-nav flex-1 p-3 sm:p-4 overflow-y-auto">
                <ul className="space-y-2">
                    {menuItems.map((item) => (
                        <li key={item.path}>
                            <Link
                                to={item.path}
                                onClick={() => handleMenuClick(item.path)}
                                className={`sidebar-menu-item flex items-center gap-3 px-4 py-3 rounded-lg transition min-h-[44px] ${isSidebarCollapsed && !isMobileVersion ? 'justify-center' : ''} relative`}
                                style={{
                                    backgroundColor: isActive(item.path) ? 'rgba(196, 158, 112, 0.2)' : 'transparent',
                                    color: isActive(item.path) ? '#c49e70' : '#f1e9dd',
                                    borderLeft: isActive(item.path) ? '3px solid #c49e70' : '3px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive(item.path)) {
                                        e.currentTarget.style.backgroundColor = 'rgba(196, 158, 112, 0.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive(item.path)) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                                title={isSidebarCollapsed && !isMobileVersion ? item.label : ''}
                            >
                                <item.icon size={20} />
                                {(!isSidebarCollapsed || isMobileVersion) && (
                                    <span className="flex items-center gap-2 flex-1">
                                        {item.label}
                                        {item.path === '/inbox' && unreadMessageCount > 0 && (
                                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
                                                {unreadMessageCount}
                                            </span>
                                        )}
                                    </span>
                                )}
                                {(isSidebarCollapsed && !isMobileVersion) && item.path === '/inbox' && unreadMessageCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        {unreadMessageCount}
                                    </span>
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Logout Button */}
            <div className="p-3 sm:p-4 border-t" style={{ borderColor: '#2d4a44' }}>
                <button
                    onClick={() => {
                        logout();
                        navigate('/login');
                        if (isMobile) {
                            closeMobileMenu();
                        }
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full transition min-h-[44px] ${isSidebarCollapsed && !isMobileVersion ? 'justify-center' : ''}`}
                    style={{ color: '#f1e9dd' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(196, 158, 112, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={isSidebarCollapsed && !isMobileVersion ? 'Logout' : ''}
                >
                    <LogOut size={20} />
                    {(!isSidebarCollapsed || isMobileVersion) && <span>Logout</span>}
                </button>
            </div>
        </>
    );

    return (
        <>
            {/* Mobile Drawer - Slide in from left, overlay with backdrop */}
            <div
                className={`md:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
                    isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black bg-opacity-50"
                    onClick={closeMobileMenu}
                />

                {/* Drawer */}
                <div
                    className={`absolute left-0 top-0 bottom-0 w-72 flex flex-col transform transition-transform duration-300 ${
                        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                    style={{ backgroundColor: '#213b36', color: '#f1e9dd' }}
                >
                    <SidebarContent isMobileVersion={true} />
                </div>
            </div>

            {/* Desktop Sidebar - Always visible, collapsible, fixed position */}
            <div
                className={`hidden md:flex fixed left-0 top-0 bottom-0 flex-col transition-all duration-300 z-40 ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}
                style={{ backgroundColor: '#213b36', color: '#f1e9dd' }}
            >
                <SidebarContent isMobileVersion={false} />
            </div>
        </>
    );
};

export default Sidebar;
